/* Selector tile — configuration product tile and its price columns */

export const selectorTileParts = [
  'list',
  'tile',
  'tileSelected',
  'tileImageWrapper',
  'tileImage',
  'tileContent',
  'tileBadge',
  'tileBadgeSale',
  'tileBadgeNew',
  'tileBadgePopular',
  'tileTitle',
  'tileDescription',
  'tileViewDetails',
  'tilePrice',
  'tilePriceMin',
  'tilePriceMax',
  'tilePriceFrom',
  'tilePriceFromSale',
  'tilePriceValue',
  'tilePriceValueSale',
  'tilePriceStrike',
  'tileRadio',
  'tileRadioDot'
];

export const selectorTileBaseStyle = {
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  // Unselected tile (Pale Grey #DDD — using gray.100 as closest token)
  tile: {
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'brandColors.paleGrey',
    borderRadius: 'base',
    cursor: 'pointer',
    display: 'flex',
    gap: {base: 3, lg: 4},
    marginBottom: 2,
    paddingX: {base: 4, lg: 4},
    paddingY: {base: 3, lg: 4},
    transition: 'border-color 0.15s ease',
    width: '100%',
    _hover: {
      borderColor: 'grayAlpha.90',
    },
    _focus: {
      boxShadow: 'outline',
      outline: 'none',
    },
    // No thumbnail: content column takes the freed width; price + radio stay
    // flex-shrink: 0 so long titles wrap inside content and never overlap price.
    sx: {
      '&.choose-your-layout-tile--no-image': {
        '& .choose-your-layout-tile-content': {
          flex: '1 1 auto',
          minWidth: 0,
        },
        '& .product-tile__name': {
          maxWidth: '100%',
        },
        '& .choose-your-layout-tile-price': {
          flexShrink: 0,
        },
        '& .choose-your-layout-tile-radio': {
          flexShrink: 0,
        },
      },
    },
  },
  // Selected tile overrides — spread onto tile when isSelected
  tileSelected: {
    border: '2px solid',
    borderColor: 'black',
    padding: '15px',
  },
  tileImageWrapper: {
    aspectRatio: '1',
    backgroundColor: 'transparent',
    flexShrink: 0,
    height: 12.5,
    maxWidth: '100%',
    overflow: 'hidden',
    width: 12.5,
  },
  tileImage: {
    height: '100%',
    objectFit: 'contain',
    width: '100%',
  },
  tileContent: {
    flex: 1,
    minWidth: 0,
    gap: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  // Badge: regular vs sale — only differs in backgroundColor (gray vs red), text always white
  tileBadge: {
    alignItems: 'center',
    backgroundColor: 'brandColors.greyscale90',
    borderRadius: 'base',
    color: '#FFF',
    display: 'inline-flex',
    fontFamily: 'body',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 600,
    gap: '10px',
    height: '18px',
    justifyContent: 'center',
    lineHeight: '120%',
    maxWidth: 'fit-content',
    padding: {'base': '2px 4px', 'lg': '2px 6px'},
    textAlign: 'center',
  },
  tileBadgeSale: {
    backgroundColor: 'secondary.summerRed',
    color: 'white',
    textTransform: 'none',
  },
  tileBadgeNew: {
    backgroundColor: 'brandColors.greyscale90',
    color: 'white',
  },
  tileBadgePopular: {
    backgroundColor: 'brandColors.greyscale90',
    color: 'white',
  },
  tileTitle: {
    color: {base: 'brandColors.primaryText90', lg: 'brandColors.greyscale90'},
    fontFamily: 'body',
    fontSize: {base: 'sm', lg: 'md'},
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 1.2,
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
    maxWidth: {base: '142px', lg: '165px'},
  },
  tileDescription: {
    color: 'greyscale.70',
    fontSize: {base: '1xs', lg: 'xs'},
    lineHeight: '1.2',
    marginTop: 1,
  },
  // "View details" underline link inside each tile (mobile only)
  tileViewDetails: {
    color: {base: 'brandColors.viewDetailsLink', lg: 'brandColors.primaryDarkLinen'},
    cursor: 'pointer',
    display: 'inline',
    fontSize: {base: '1xs', lg: 'xs'},
    fontWeight: {base: 600, lg: 400},
    lineHeight: '1.4',
    marginTop: 1,
    textDecoration: 'underline',
    _hover: {
      color: 'grayAlpha.90',
    },
  },
  // Price column (right-aligned): two parts — price min, price max (Figma 6492-44524)
  tilePrice: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    textAlign: 'right',
  },
  tilePriceMin: {
    color: 'grayAlpha.90',
    fontSize: 'xs',
    fontWeight: 'semibold',
    lineHeight: '1.2',
    maxWidth: 13,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 0.5,
    justifyContent: 'flex-end',
  },
  tilePriceMax: {
    color: 'grayAlpha.90',
    fontSize: 'md',
    fontWeight: 'semibold',
    lineHeight: '1.4',
  },
  tilePriceFrom: {
    color: 'grayAlpha.90',
    fontFamily: 'body',
    fontSize: {base: '1xs', lg: 'xs'},
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 1.2,
    textAlign: 'right',
  },
  tilePriceFromSale: {
    color: 'brandColors.newSale',
  },
  tilePriceValue: {
    color: 'grayAlpha.90',
    fontFamily: 'body',
    fontSize: {base: '1xs', lg: 'md'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    lineHeight: {base: 1.2, lg: 1.4},
    textAlign: 'right',
    paddingBottom: 0.5,
  },
  tilePriceValueSale: {
    color: 'brandColors.newSale',
  },
  // Strikethrough (list) price
  tilePriceStrike: {
    color: 'greyscale.70',
    fontFamily: 'body',
    fontSize: {base: '13px', lg: '12px'},
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 1.2,
    textAlign: 'right',
    textDecoration: 'line-through',
  },
  // Radio circle
  tileRadio: {
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'brandColors.linen03',
    borderRadius: 'full',
    display: 'flex',
    flexShrink: 0,
    height: 8,
    justifyContent: 'center',
    width: 8,
  },
  tileRadioDot: {
    bg: 'brandColors.greyscale',
    borderRadius: 'full',
    height: 5.5,
    width: 5.5,
  },
};
